import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Check, MoreVertical, Pencil, Plus, X } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import type { StockCategory } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Category management, folded out of its former whole-tab home (F-FE-N103).
 *
 * What the tab never had and this has: duplicate names are refused with an
 * inline error (backed by a DB unique index), ordering is two arrow buttons
 * instead of an invisible number field, archived items are visible in the
 * counts (they block deletion — the "0 items but can't delete" mystery), and
 * a populated or duplicated category can be MERGED into another instead of
 * being an undeletable husk.
 */
export function ManageCategoriesDialog({ open, onClose, showToast }: {
  open: boolean
  onClose: () => void
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['stockCategories'],
    queryFn: () => apiClient.getStockCategories(),
    enabled: open,
  })
  const categories: StockCategory[] = data?.data ?? []

  const [newName, setNewName] = useState('')
  const [newError, setNewError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [mergeSource, setMergeSource] = useState<StockCategory | null>(null)
  const [mergeTarget, setMergeTarget] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<StockCategory | null>(null)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['stockCategories'] })
    qc.invalidateQueries({ queryKey: ['stockItems'] })
    qc.invalidateQueries({ queryKey: ['stockSummary'] })
  }
  const duplicateMessage = (e: Error) =>
    (e as ApiClientError).code === 'duplicate_category'
      ? 'A category with this name already exists.'
      : e.message || 'Something went wrong'

  const createMut = useMutation({
    mutationFn: () => apiClient.createStockCategory({ name: newName.trim(), sort_order: categories.length }),
    onSuccess: () => { setNewName(''); setNewError(null); invalidate() },
    onError: (e: Error) => setNewError(duplicateMessage(e)),
  })
  const updateMut = useMutation({
    mutationFn: (vars: { id: string; patch: { name?: string; description?: string; is_active?: boolean } }) =>
      apiClient.updateStockCategory(vars.id, vars.patch as any),
    onSuccess: () => { setEditingId(null); setEditError(null); invalidate() },
    onError: (e: Error) => setEditError(duplicateMessage(e)),
  })
  const reorderMut = useMutation({
    mutationFn: (ids: string[]) => apiClient.reorderStockCategories(ids),
    onSuccess: invalidate,
    onError: (e: Error) => showToast('error', e.message || 'Failed to reorder'),
  })
  const mergeMut = useMutation({
    mutationFn: () => apiClient.mergeStockCategory(mergeSource!.id, mergeTarget),
    onSuccess: (res) => { showToast('success', res.message ?? 'Merged'); setMergeSource(null); setMergeTarget(''); invalidate() },
    onError: (e: Error) => showToast('error', e.message || 'Merge failed'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteStockCategory(id),
    onSuccess: () => { setDeleteTarget(null); showToast('success', 'Category deleted'); invalidate() },
    onError: (e: Error) => { setDeleteTarget(null); showToast('error', e.message || 'Delete failed') },
  })

  const move = (index: number, dir: -1 | 1) => {
    const next = [...categories]
    const j = index + dir
    if (j < 0 || j >= next.length) return
    ;[next[index], next[j]] = [next[j], next[index]]
    reorderMut.mutate(next.map(c => c.id))
  }

  const startEdit = (c: StockCategory) => {
    setEditingId(c.id)
    setEditName(c.name)
    setEditDescription(c.description ?? '')
    setEditError(null)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
          <DialogDescription>
            Order here is the order the Items list groups by. Categories holding items can be merged, not deleted.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex items-start gap-2"
          onSubmit={e => { e.preventDefault(); if (newName.trim()) createMut.mutate() }}
        >
          <div className="flex-1">
            <Input placeholder="New category name" value={newName} onChange={e => { setNewName(e.target.value); setNewError(null) }} />
            {newError && <p className="mt-1 text-xs text-danger-fg">{newError}</p>}
          </div>
          <Button type="submit" disabled={!newName.trim() || createMut.isPending}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </form>

        <ul className="divide-y rounded-lg border">
          {categories.map((c, i) => (
            <li key={c.id} className={`px-3 py-2.5 ${!c.is_active ? 'opacity-60' : ''}`}>
              {editingId === c.id ? (
                <form
                  className="space-y-2"
                  onSubmit={e => {
                    e.preventDefault()
                    if (!editName.trim()) return
                    updateMut.mutate({ id: c.id, patch: { name: editName.trim(), description: editDescription.trim() || undefined } })
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Input value={editName} onChange={e => { setEditName(e.target.value); setEditError(null) }} className="h-8" autoFocus />
                    <Button type="submit" size="sm" className="h-8" disabled={updateMut.isPending || !editName.trim()}>
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setEditingId(null)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <Input value={editDescription} onChange={e => setEditDescription(e.target.value)} placeholder="Description (optional)" className="h-8 text-sm" />
                  {editError && <p className="text-xs text-danger-fg">{editError}</p>}
                </form>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button type="button" aria-label={`Move ${c.name} up`} className="text-muted-foreground/60 hover:text-foreground disabled:opacity-30" disabled={i === 0 || reorderMut.isPending} onClick={() => move(i, -1)}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" aria-label={`Move ${c.name} down`} className="text-muted-foreground/60 hover:text-foreground disabled:opacity-30" disabled={i === categories.length - 1 || reorderMut.isPending} onClick={() => move(i, 1)}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {c.item_count ?? 0} item{(c.item_count ?? 0) === 1 ? '' : 's'}
                        {(c.archived_item_count ?? 0) > 0 ? ` · ${c.archived_item_count} archived` : ''}
                      </Badge>
                      {!c.is_active && <Badge variant="outline" className="text-xs shrink-0">Hidden</Badge>}
                    </div>
                    {c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0" title={c.is_active ? 'Shown in pickers and summaries' : 'Hidden from pickers and summaries'}>
                    <Switch
                      checked={c.is_active}
                      onCheckedChange={(v) => updateMut.mutate({ id: c.id, patch: { is_active: v } })}
                      aria-label={`${c.name} active`}
                    />
                  </div>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label={`Edit ${c.name}`} onClick={() => startEdit(c)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" aria-label={`More for ${c.name}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setMergeSource(c); setMergeTarget('') }}>
                        Merge into…
                      </DropdownMenuItem>
                      {(c.item_count ?? 0) + (c.archived_item_count ?? 0) === 0 ? (
                        <DropdownMenuItem className="text-danger-fg" onClick={() => setDeleteTarget(c)}>
                          Delete
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem disabled>
                          Delete (has items — merge instead)
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </li>
          ))}
          {categories.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">No categories yet — add one above.</li>
          )}
        </ul>

        {mergeSource && (
          <div className="rounded-lg border border-warning-border bg-warning-surface/30 p-3 space-y-2">
            <p className="text-sm">
              Merge <span className="font-semibold">{mergeSource.name}</span> into:
            </p>
            <select
              className="w-full h-9 rounded-md border bg-background px-2 text-sm"
              value={mergeTarget}
              onChange={e => setMergeTarget(e.target.value)}
              aria-label="Merge target category"
            >
              <option value="">Choose a category…</option>
              {categories.filter(c => c.id !== mergeSource.id).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Every item — including {(mergeSource.archived_item_count ?? 0) > 0 ? 'archived ones' : 'any archived ones'} — moves across, then “{mergeSource.name}” is removed. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setMergeSource(null)}>Cancel</Button>
              <Button size="sm" className="flex-1" disabled={!mergeTarget || mergeMut.isPending} onClick={() => mergeMut.mutate()}>
                {mergeMut.isPending ? 'Merging…' : 'Merge'}
              </Button>
            </div>
          </div>
        )}

        {deleteTarget && (
          <div className="rounded-lg border border-danger-border bg-danger-surface/30 p-3 space-y-2">
            <p className="text-sm">Delete <span className="font-semibold">{deleteTarget.name}</span>? It holds no items. This cannot be undone.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button size="sm" variant="destructive" className="flex-1" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate(deleteTarget.id)}>
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
